import React, { Fragment, useState, useRef } from "react";
import { useOutletContext} from "react-router-dom";
import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import resolveConfig from "tailwindcss/resolveConfig";
import tailwindConfig from "../../tailwind.config.js";
import OrganisationCostOverviewTable from "../../components/OrganisationCostOverviewTable.jsx";
import dayjs from "dayjs";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";
import { Dropdown } from "../../components/form-components/Dropdown.jsx";
import { retrieveOrganisationLevels } from "./api.js";
import { useQuery } from "@tanstack/react-query";
import { useKeycloak } from "@react-keycloak/web";
import { useFilterStoreWithMomentDates } from "../../store/filterStore.js";

const colors = resolveConfig(tailwindConfig).theme.colors;

const API_URL = process.env.REACT_APP_API_URL;

const OrganisationalCostOverview = () => {
    const [filters, ] = useState([]);
    const [organisationalLevels, setOrganisationalLevels] = useState([]);
    const [organisationLevel1, setOrganisationLevel1] = useState([]);
    const [organisationLevel2, setOrganisationLevel2] = useState([]);
    const { keycloak } = useKeycloak();

        
    // Use Zustand store for date state with moment dates
    const { startDate,  endDate } = useFilterStoreWithMomentDates();
    const { selectedOrganisationLevel, setSelectedOrganisationLevel } = useOutletContext();

    // initialization for the variables for the fuzzy search, the filters and the grouping
	const filtersRef = useRef();
	filtersRef.current = filters;

    /**
     * Query hook to fetch organization levels data
     * 
     * This query:
     * 1. Makes a request to retrieve organization levels using the JWT token
     * 2. On success, transforms the data into an array of objects with:
     *    - label: The business name of the organization
     *    - value: "Organisation L" + level number
     * 3. Updates the organisationalLevels state with the transformed data
     * 
     * @returns {UseQueryResult} Query result object containing organization levels data
     */
    useQuery(
		["organisationLevels"],
		() =>
			retrieveOrganisationLevels(keycloak.token),
		{
			onSuccess: (data) => {
                var tmpData = [];
                if (data.OrganisationLevels !== undefined) {
                    data.OrganisationLevels.forEach((element) => {
                        var tmpElement = {
                            label: element.business_name,    
                            value: element.level
                        };
                        tmpData.push(tmpElement);
                    });
                }

				setOrganisationalLevels(tmpData);
                setSelectedOrganisationLevelWhenNotSelected(tmpData[0]);
                setOrganisationLevel1(tmpData[0].label);
                setOrganisationLevel2(tmpData[1].label);
			},
			enabled: keycloak.token !== "",
			staleTime: 5000, // Optional: Prevent frequent refetching
		}
	);

    /**
     * Query hook to fetch organization cost overview data
     * 
     * This query:
     * 1. Makes a request to get organization cost overview using the JWT token
     * 2. Processes the response by determining reporting months and pivoting data
     * 3. Returns processed data with total columns and sorted rows
     * 
     * @returns {UseQueryResult} Query result object containing processed organization cost overview data
     */
    const organisationCostOverviewQuery = useQuery(
        ["organisationCostOverview", selectedOrganisationLevel?.value, startDate, endDate],
        async () => {
            if (!selectedOrganisationLevel || selectedOrganisationLevel.value < 0) {
                return { organisationCostOverview: [], reportingMonths: [] };
            }

            const headers = new Headers();
            headers.append("Content-Type", "application/json");
            headers.append("Authorization", "Bearer " + keycloak.token);

            const requestOptions = {
                method: "GET",
                headers: headers,
            };

            const response = await fetch(
                API_URL + "/get-organisation-cost-overview?level=" + 
                selectedOrganisationLevel.value + 
                "&startDate=" + dayjs(startDate).format("YYYY-MM-DD") + 
                "&endDate=" + dayjs(endDate).format("YYYY-MM-DD"), 
                requestOptions
            );

            const json = await response.json();

            if (json.OrganisationCostOverview !== null) {
                const tmpReportingMonths = determineReportingMonths(json.OrganisationCostOverview, "month");
                
                var tmpOrganisationCostOverview = addTotalColumn(pivotData(json.OrganisationCostOverview, tmpReportingMonths));
                const totalRow = getTotalRow(tmpOrganisationCostOverview, tmpReportingMonths);
                tmpOrganisationCostOverview.unshift(totalRow);

                // Sort the non-total rows by organisation and sub_organisation
                tmpOrganisationCostOverview = [
                    ...tmpOrganisationCostOverview.slice(1).sort((a, b) => {
                        // First compare organisations
                        const orgCompare = a.organisation.localeCompare(b.organisation);
                        if (orgCompare !== 0) return orgCompare;
                        
                        // For same organisation, empty sub_organisation goes first
                        if (!a.sub_organisation) return -1;
                        if (!b.sub_organisation) return 1;
                        
                        // Otherwise sort by sub_organisation
                        return a.sub_organisation.localeCompare(b.sub_organisation);
                    }),
                    totalRow // Keep total row last
                ];

                return {
                    organisationCostOverview: tmpOrganisationCostOverview,
                    reportingMonths: tmpReportingMonths
                };
            } else {
                return {
                    organisationCostOverview: [],
                    reportingMonths: []
                };
            }
        },
        {
            enabled: keycloak.token !== "" && selectedOrganisationLevel !== null && selectedOrganisationLevel.value >= 0,
            staleTime: 30000, // 30 seconds
            refetchOnWindowFocus: false,
        }
    );

    /**
     * Sets the selected organization level if none is currently selected
     * 
     * This function:
     * 1. Checks if selectedOrganisationLevel is null
     * 2. If null, sets it to the provided level
     * 
     * @param {Object} level - The organization level object to set
     * @param {string} level.label - The display label for the level
     * @param {number} level.value - The numeric value of the level
     * @returns {void}
     */
    function setSelectedOrganisationLevelWhenNotSelected(level) {
        if (selectedOrganisationLevel === null) {
            setSelectedOrganisationLevel(level);
        }
    }

    /**
     * Transforms organization data into a format suitable for dropdown/select components
     * 
     * This function maps each organization element to an object with standardized fields:
     * - category: The organization level description
     * - category_description: The organization level description (duplicate for consistency)
     * - label: The organization name to display in UI
     * - value: The organization name to use as the select value
     * 
     * @param {Array} data - Array of organization objects containing level_description and name
     * @returns {Array} Array of transformed organization objects with standardized fields
     */
    
    /**
     * Determines the list of reporting months between the earliest and latest dates in the data
     * 
     * This function:
     * 1. Takes an array of data objects containing dates and a granularity parameter
     * 2. For monthly granularity:
     *    - Extracts unique dates from the data
     *    - Finds the min and max dates
     *    - Generates an array of all months between min and max dates
     * 3. Returns array of dates formatted as YYYY-MM-DD strings
     * 
     * @param {Array} data - Array of objects containing date field
     * @param {string} granularity - Time granularity ('month' supported)
     * @returns {Array} Array of date strings in YYYY-MM-DD format representing each month
     */
    function determineReportingMonths(data, granularity) {
        if (granularity === "month") {
            const dates = [...new Set(data.map(row => dayjs(row.date).format("YYYY-MM-DD")))];
            const minDate = new Date(Math.min(...dates.map(d => new Date(d))));
            const maxDate = new Date(Math.max(...dates.map(d => new Date(d))));

            const allMonths = [];
            let currentDate = new Date(minDate);
            
            while (currentDate <= maxDate) {
                allMonths.push(dayjs(currentDate).format("YYYY-MM-DD"));
                currentDate = dayjs(currentDate).add(1, 'month').toDate();
            }

            return allMonths;
        }
    }

    /**
     * Adds a total column to pivoted cost allocation data by summing all cost columns
     * 
     * This function:
     * 1. Takes an array of pivoted cost allocation objects where costs are spread across columns
     * 2. For each row, identifies all columns that start with 'C' (cost columns)
     * 3. Calculates the sum of values in those cost columns
     * 4. Adds a new 'total' property to each row with the calculated sum
     * 
     * @param {Array} pivotedData - Array of pivoted cost allocation objects
     * @returns {Array} Array of objects with original data plus total column
     */
    function addTotalColumn(pivotedData) {
        const totalCosts = pivotedData.map(row => {
            // Get all keys that start with 'C'
            const costColumns = Object.keys(row).filter(key => key.startsWith('C'));
            
            // Calculate sum of all cost columns
            const total = costColumns.reduce((sum, key) => {
                return sum + (row[key] || 0);
            }, 0);

            // Return new object with all existing properties plus total
            return {
                ...row,
                total: total
            };
        });

        return totalCosts;
    }   

    /**
     * Calculates a total row by summing costs across all data rows
     * 
     * This function:
     * 1. Creates a total row object with default "Total" values for text columns
     * 2. Initializes cost columns (C1, C2, etc) and grand total to 0
     * 3. Iterates through the pivoted data rows and sums up:
     *    - Each individual cost column (C1, C2, etc)
     *    - The grand total of all costs
     * 
     * @param {Array} pivotedData - Array of pivoted cost allocation objects
     * @param {Array} reportingMonths - Array of reporting month dates
     * @returns {Object} Total row object with summed costs
     */
    function getTotalRow(pivotedData, reportingMonths) {
        var totalRow = {
            organisation: "Total",
            sub_organisation: "", 
            costs: "",
        }

        for (let i = 0; i < reportingMonths.length; i++) {
            totalRow["C" + (i + 1)] = 0;
        }

        totalRow["total"] = 0;

        // Sum up each C column across all rows
        pivotedData.forEach(row => {
            for (let i = 0; i < reportingMonths.length; i++) {
                const columnKey = "C" + (i + 1);
                totalRow[columnKey] += (row[columnKey] || 0);
            }
            totalRow["total"] += (row["total"] || 0);
        });

        return totalRow;
    }

    /**
     * Gets the index of a date within an array of reporting periods
     * 
     * This function:
     * 1. Takes an array of reporting period dates and a target date
     * 2. Formats the target date to match the reporting period format (YYYY-MM-DD)
     * 3. Returns the index where the formatted date matches a reporting period
     * 4. Returns -1 if no match is found
     * 
     * @param {Array} reportingPeriods - Array of reporting period dates in YYYY-MM-DD format
     * @param {string|Date} date - Date to find the index for
     * @returns {number} Index of the matching reporting period, or -1 if not found
     */
    function getReportingPeriodIndex(reportingPeriods, date) {
        return reportingPeriods.findIndex(period => period === dayjs(date).format("YYYY-MM-DD"));
    }
    

    /**
     * Pivots cost allocation data from a row-based format to a column-based format
     * 
     * This function:
     * 1. Takes an array of cost allocation records with allocation_type, allocation_method, 
     *    allocated_from, date and allocated_costs fields
     * 2. Groups the records by unique combinations of allocation_type, allocation_method 
     *    and allocated_from
     * 3. For each group, creates columns for each date containing the allocated_costs
     * 4. Returns an array of objects where each object represents one unique allocation
     *    with costs spread across date-based columns
     * 
     * @param {Array} data - Array of cost allocation records to pivot
     * @returns {Array} Array of pivoted allocation objects with costs as columns
     */
    function pivotData(data, reportingPeriods) {
        // Step 1: Group data by unique keys (allocation_type, allocation_method, allocated_from)
        const grouped = {};
                
        data.forEach(row => {
            const key = `${row.organisation}|${row.sub_organisation}`;
            
            if (!grouped[key]) {
                grouped[key] = { 
                    organisation: row.organisation,
                    sub_organisation: row.sub_organisation,
                };
            }
           
            // Add the date as a column with the allocated_costs value
            const dateKey = "C" + (getReportingPeriodIndex(reportingPeriods, row.date) + 1);
            grouped[key][dateKey] = row.costs;
        });

        // Step 2: Convert grouped object back to an array
        return Object.values(grouped);
    }



    return (
        <Fragment>
            <AdministrationHeader
				title="Organisational Overview"
				className="text-text-3"
                icon={icon({name: "sitemap", style: "light"})}
			/>
            <div className="pl-6 text-left flex text-text-2 mt-6">
                <div className="ml-4 pt-1">
					<div>
						<div className="mr-8">
							<Dropdown
                                valuePrefix="Level : "
								options={organisationalLevels}
								dropdownWidth={340}
								selectedValue={selectedOrganisationLevel}
								setSelectedValue={setSelectedOrganisationLevel}
                                backgroundColor={colors.primary}
                                // borderColor={colors.pink3}
                                textColor={colors.white}
							/>
						</div>
					</div>
				</div>
			</div>

            <div className="text-left flex flex-auto flex-col ml-10 border-solid border-border">
                <div className="flex flex-row">
                    {/* <KPI
                        title="Total Costs"
                        currentValue={totalCost}
                        trendData={costTrend}
                        format="currency"
                        showChange={false}
                        backgroundColor={"lightblue10"}
                        style="small"
                    /> */}
                </div>

                <div className="bg-card rounded-card border border-card-bd py-2 my-2 h-screen flex flex-auto flex-col">
                    <OrganisationCostOverviewTable 
                        sourceData={organisationCostOverviewQuery.data?.organisationCostOverview || []} 
                        reportingPeriods={organisationCostOverviewQuery.data?.reportingMonths || []}
                        organisationLevel1={organisationLevel1}
                        organisationLevel2={organisationLevel2}
                    />
                </div>
            </div>
        </Fragment>
    );
};

export default OrganisationalCostOverview;

